import { Link } from '@inertiajs/react';

const PATH_ALIASES = {
    selections: '/manage/selections',
    players: '/players',
    communicate: '/communicate',
    settings: '/settings',
    'manage/selections': '/manage/selections',
    'manage/selections/team-view': '/manage/selections/team-view',
    'squads-teams': '/settings/squads-teams',
    branding: '/settings/branding',
    'manage-access': '/settings/manage-access',
};

export function normalizeAssistantUrl(url) {
    if (!url || typeof url !== 'string') {
        return url;
    }

    let normalized = url.trim();

    if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
        try {
            const parsed = new URL(normalized);

            return parsed.pathname + parsed.search;
        } catch {
            return normalized;
        }
    }

    if (normalized.startsWith('#/')) {
        normalized = normalized.slice(2);
    } else if (normalized.startsWith('#')) {
        normalized = normalized.slice(1);
    }

    normalized = normalized.replace(/^\/+/, '');

    if (PATH_ALIASES[normalized]) {
        return PATH_ALIASES[normalized];
    }

    if (normalized.startsWith('settings/')) {
        return `/${normalized}`;
    }

    if (normalized.startsWith('manage/')) {
        return `/${normalized}`;
    }

    return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

const MARKDOWN_PATTERN = /(\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\))/g;

function formatInlineMarkdown(text, keyPrefix) {
    if (!text) {
        return [];
    }

    const nodes = [];
    let lastIndex = 0;
    let match;

    const pattern = new RegExp(MARKDOWN_PATTERN.source, 'g');

    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(text.slice(lastIndex, match.index));
        }

        if (match[2]) {
            nodes.push(
                <strong key={`${keyPrefix}-bold-${match.index}`} className="font-semibold">
                    {match[2]}
                </strong>,
            );
        } else if (match[3] && match[4]) {
            const href = normalizeAssistantUrl(match[4]);

            nodes.push(
                <Link
                    key={`${keyPrefix}-link-${match.index}`}
                    href={href}
                    className="text-primary-blue-300 underline hover:text-primary-blue-200"
                >
                    {match[3]}
                </Link>,
            );
        }

        lastIndex = pattern.lastIndex;
    }

    if (lastIndex < text.length) {
        nodes.push(text.slice(lastIndex));
    }

    return nodes.length > 0 ? nodes : [text];
}

export function formatMessageContent(content) {
    if (!content) {
        return null;
    }

    const nonEmptyParagraphs = content
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);

    if (nonEmptyParagraphs.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-3">
            {nonEmptyParagraphs.map((paragraph, paragraphIndex) => {
                const lines = paragraph.split('\n');
                const children = [];

                lines.forEach((line, lineIndex) => {
                    children.push(...formatInlineMarkdown(line, `p${paragraphIndex}-l${lineIndex}`));

                    if (lineIndex < lines.length - 1) {
                        children.push(<br key={`br-${paragraphIndex}-${lineIndex}`} />);
                    }
                });

                return (
                    <p key={`p-${paragraphIndex}`} className="mb-0">
                        {children}
                    </p>
                );
            })}
        </div>
    );
}

export function parseNavigationLinks(content) {
    const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
    const links = [];
    let match;

    while ((match = linkPattern.exec(content)) !== null) {
        links.push({
            label: match[1],
            url: normalizeAssistantUrl(match[2]),
        });
    }

    return links;
}
